import type { ManageAPI } from "~/manage-api/types";
import { debugLog } from './debugUtils.js';
import { 
  type StorePinData
} from './storeTypes.js';

/**
 * Process all stores and create pin data for each floor
 */
export function processAllStores(
  contextStores: ManageAPI.Store[],
  floorsData: any[]
): {
  cachedPinsByFloor: Map<number, StorePinData[]>,
  initialized: boolean
} {
  const cachedPinsByFloor = new Map<number, StorePinData[]>();
  
  debugLog(`Processing ${contextStores.length} stores from context`, 'containerDetection');
  
  // First, create a mapping of floor indexes to floor IDs from store data
  const floorIndexToIdMap = new Map<number, number>();
  
  // Process each store to extract floor mapping information
  contextStores.forEach(store => {
    if (!store.storeLocations) return;
    
    store.storeLocations.forEach((location: any) => {
      if (location.floorIndex !== undefined && store.floor?.id !== undefined) {
        // Map floor index to floor ID
        floorIndexToIdMap.set(location.floorIndex, store.floor.id);
      }
    });
  });
  
  debugLog('Floor index to ID mapping: ' + 
    Array.from(floorIndexToIdMap.entries())
      .map(([index, id]) => `Floor index ${index} → ID ${id}`)
      .join(', '), 'shapeMatching'
  );
  
  // Process each store
  contextStores.forEach(store => {
    if (!store.storeLocations) return;
    
    // Process each store location (pins)
    store.storeLocations.forEach((location: any) => {
      if (!location.pinLocation || 
          location.pinLocation.x === undefined || 
          location.pinLocation.y === undefined || 
          location.floorIndex === undefined) {
        return;
      }
      
      // Get floor ID from the mapping based on floor index
      let floorId: number | null = null;
      
      // First try to get from our mapping
      if (floorIndexToIdMap.has(location.floorIndex)) {
        floorId = floorIndexToIdMap.get(location.floorIndex) || null;
      } 
      // Try direct assignment if available
      else if (location.floorId) {
        floorId = location.floorId;
      } 
      // Fallback to store's floor
      else if (store.floor?.id) {
        floorId = store.floor.id;
      }
      
      // Skip if we couldn't determine the floor ID
      if (floorId === null) return;
      
        // Create pin data
        const pinData: StorePinData = {
        id: store.id.toString(),
        name: store.name,
        x: location.pinLocation.x,
        y: location.pinLocation.y,
        floorId: floorId,
        shapeId: null, // Will be populated when we find the shape under this pin
        baseShapeId: null,
        relatedShapeIds: [],
        storeData: store // Keep reference to original store data
      };
      
        // Debug logging for raw coordinates
        debugLog(`Store ${store.id} (${store.name}): Raw backend coords (${location.pinLocation.x}, ${location.pinLocation.y}) on floor ${floorId}`, 'containerDetection');
      
      // Add to cache by floor
      if (!cachedPinsByFloor.has(floorId)) {
        cachedPinsByFloor.set(floorId, []);
      }
      cachedPinsByFloor.get(floorId)?.push(pinData);
    });
  });
  
  // Debug logging for pre-computed pins
  debugLog(`Pre-computed pins for ${cachedPinsByFloor.size} floors:`, 'containerDetection');
  cachedPinsByFloor.forEach((pins, floorId) => {
    debugLog(`- Floor ${floorId}: ${pins.length} pins`, 'containerDetection');
  });
  
  return {
    cachedPinsByFloor,
    initialized: true
  };
} 